import { CHAT_BOT_APP_DEFAULT_SETTINGS } from "@jugl-web/domain-resources/chat-bot/consts";
import { useRestApiProvider } from "@jugl-web/rest-api";
import {
  DataLoadingWrapper,
  MultiSectionLayout,
  TextInput,
} from "@jugl-web/ui-components";
import { isValidHttpUrl, useToast, useTranslations } from "@jugl-web/utils";
import {
  ANONYMOUS_SESSION_ENTITY_ID_KEY,
  CX_TRIAL_PROCEEDED_KEY,
  getStorageItem,
  saveItemToStorage,
  CX_BUSINESS_NAME_KEY,
} from "@jugl-web/utils/storage";
import { NavigateToDefaultEntity } from "@web-src/features/app/components/NavigateToDefaultEntity/NavigateToDefaultEntity";
import { selectAuthState } from "@web-src/features/auth/authSlice";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { TrialFullPageLoader } from "@web-src/modules/trial/pages/CreateBoardInTrial/components/TrialFullPageLoader/TrialFullPageLoader";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useUpdateChatBotSettings } from "@jugl-web/domain-resources/chat-bot/hooks/useUpdateChatBotSettings";
import { Button, InteractiveContainer } from "@jugl-web/ui-kit";
import { KnowledgeItem } from "@jugl-web/rest-api/ai-workflows/types";
import { ReactComponent as AlertIcon } from "./assets/alert.svg";
import { ReactComponent as Logo } from "./assets/logo.svg";
import { OverviewVideo } from "./components/OverviewVideo";
import { TrialHelpWidget } from "./components/TrialHelpWidget";
import { useCXSignupDialog } from "../../components/CXSignupDialog/useCXSignupDialog";
import { useKnowledgeTraining } from "../../providers/KnowledgeTrainingProvider";
import { DEFAULT_AGENT_CONFIG } from "../CXAIAgentsPage/consts";

const CXTrialPageContent: React.FC = () => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { aiWorkflowsApi, integrationsApi } = useRestApiProvider();
  const { navigateToPage } = useNavigation();
  const { entityId } = useAnonymousSession();
  const CXSignupDialog = useCXSignupDialog();

  const [getListLinks] = aiWorkflowsApi.useLazyGetListLinksQuery();
  const [uploadLinkKnowledge] = aiWorkflowsApi.useUploadLinkKnowledgeMutation();
  const [createAgentConfig] = integrationsApi.useCreateAgentConfigMutation();
  const [updateAgentConfig] = integrationsApi.useUpdateAgentConfigMutation();
  const [getSiteInfoQuery] = aiWorkflowsApi.useLazyGetSiteInfoQuery();
  const [deleteKnowledge] = aiWorkflowsApi.useDeleteKnowledgeMutation();
  const { trainAgent } = useKnowledgeTraining();

  const [searchParams, setSearchParams] = useSearchParams();
  const buildParam = searchParams.get("build");
  const [link, setLink] = useState(buildParam || "");

  const [isLinkInvalid, setIsLinkInvalid] = useState(false);

  const [isLoaderVisible, setIsLoaderVisible] = useState(false);

  const [isInvalid, setIsInvalid] = useState(false);

  const [isTrialLoaderFinished, setIsTrialLoaderFinished] = useState(false);
  const [isTrainAgentFinished, setIsTrainAgentFinished] = useState(false);

  const { updateChatBotSettings } = useUpdateChatBotSettings({ entityId });

  const {
    data: linksKnowledge,
    isLoading: isLinksKnowledgeLoading,
    isError: isLinksKnowledgeError,
    refetch: linksKnowledgeRefetch,
  } = aiWorkflowsApi.useGetKnowledgeQuery({
    agentId: entityId,
    entityId,
    type: "links",
  });
  const { data: existingAgentConfig, isLoading: isAgentConfigLoading } =
    integrationsApi.useGetAiAgentConfigQuery({
      entityId,
    });

  const linkKnowledge = linksKnowledge?.[0] as
    | KnowledgeItem<"links">
    | undefined;

  useEffect(() => {
    const existingLink = linkKnowledge?.data.ref;
    if (!existingLink) return;

    setLink(existingLink);
  }, [linkKnowledge?.data.ref]);

  useEffect(() => {
    if (isTrialLoaderFinished && isTrainAgentFinished) {
      navigateToPage("cxAnonymousAIAgents", undefined, { entityId });
      saveItemToStorage(CX_TRIAL_PROCEEDED_KEY, "true");
    }
  }, [entityId, isTrainAgentFinished, isTrialLoaderFinished, navigateToPage]);

  const handleTrainAgent = useCallback(
    async (knowledgeSourceId: string) => {
      const response = await trainAgent([knowledgeSourceId]);

      if (response) {
        setIsTrainAgentFinished(true);
      } else {
        setIsLoaderVisible(false);
      }
    },
    [trainAgent]
  );

  const handleUpdateChatBotSettings = useCallback(
    async (settings: { agentImage: string; agentName: string }) => {
      try {
        await updateChatBotSettings({
          agentImage: settings?.agentImage,
          agentName: settings?.agentName.trim(),
        });
        return true;
      } catch {
        setIsLoaderVisible(false);
        toast(
          t({
            id: "feedback.something-went-wrong",
            defaultMessage: "Something went wrong",
          }),
          { variant: "error" }
        );
        return false;
      }
    },
    [t, toast, updateChatBotSettings]
  );

  const handleUpdateBusinessName = useCallback(
    async (businessName: string) => {
      const normalizedBusinessName = businessName.trim();
      try {
        saveItemToStorage(CX_BUSINESS_NAME_KEY, normalizedBusinessName);
        if (existingAgentConfig) {
          await updateAgentConfig({
            entityId,
            config: {
              ...existingAgentConfig.config,
              business_name: normalizedBusinessName,
            },
          }).unwrap();
        } else {
          await createAgentConfig({
            entityId,
            config: {
              ...DEFAULT_AGENT_CONFIG,
              business_name: normalizedBusinessName,
            },
          }).unwrap();
        }
        return true;
      } catch {
        setIsLoaderVisible(false);
        toast(
          t({
            id: "feedback.something-went-wrong",
            defaultMessage: "Something went wrong",
          }),
          { variant: "error" }
        );
        return false;
      }
    },
    [
      entityId,
      t,
      toast,
      createAgentConfig,
      existingAgentConfig,
      updateAgentConfig,
    ]
  );

  useEffect(() => {
    const hasProceeded = getStorageItem<boolean>(CX_TRIAL_PROCEEDED_KEY);
    if (hasProceeded) {
      localStorage.removeItem(CX_TRIAL_PROCEEDED_KEY);
      localStorage.removeItem("session");
      localStorage.removeItem(ANONYMOUS_SESSION_ENTITY_ID_KEY);
      window.location.reload();
    }
  }, []);

  const handleCreateLinkKnowledge = useCallback(async () => {
    const linkWithProtocol = /^https?:\/\//i.test(link)
      ? link
      : `https://${link}`;
    try {
      const existingLinkKnowledge = linkKnowledge;
      const hasSameLink =
        !!existingLinkKnowledge &&
        existingLinkKnowledge.data.ref === linkWithProtocol;

      if (hasSameLink) {
        const siteInfo = await getSiteInfoQuery({
          agentId: entityId,
          entityId,
          url: linkWithProtocol,
        }).unwrap();

        setLink("");
        return {
          knowledgeSourceId: existingLinkKnowledge.data.id,
          siteInfo: siteInfo.info,
        };
      }

      if (existingLinkKnowledge) {
        await deleteKnowledge({
          agentId: entityId,
          entityId,
          type: "links",
          source_id: existingLinkKnowledge.data.id,
        }).unwrap();
      }

      const [siteInfo, listLinks] = await Promise.all([
        getSiteInfoQuery({
          agentId: entityId,
          entityId,
          url: linkWithProtocol,
        }).unwrap(),
        getListLinks({
          agentId: entityId,
          entityId,
          params: { url: linkWithProtocol },
        }).unwrap(),
      ]);

      const knowledgeSource = await uploadLinkKnowledge({
        agentId: entityId,
        entityId,
        url: listLinks.links.url,
        links: listLinks.links.links,
      }).unwrap();

      return {
        knowledgeSourceId: knowledgeSource.id,
        siteInfo: siteInfo.info,
      };
    } catch {
      toast(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong",
        }),
        { variant: "error" }
      );
      return null;
    }
  }, [
    deleteKnowledge,
    entityId,
    getListLinks,
    getSiteInfoQuery,
    link,
    linkKnowledge,
    t,
    toast,
    uploadLinkKnowledge,
  ]);

  const handleContinue = useCallback(async () => {
    setIsTrialLoaderFinished(false);
    if (!link) {
      setIsInvalid(true);
      return;
    }
    const isValid = isValidHttpUrl(link, false);
    setIsLinkInvalid(!isValid);
    if (!isValid) return;

    setIsLoaderVisible(true);
    const knowledgeData = await handleCreateLinkKnowledge();
    if (!knowledgeData) {
      setIsLoaderVisible(false);
      return;
    }
    const { siteInfo, knowledgeSourceId } = knowledgeData;

    const [isBusinessNameUpdated, isChatBotSettingsUpdated] = await Promise.all(
      [
        siteInfo.title ? handleUpdateBusinessName(siteInfo.title) : true,
        handleUpdateChatBotSettings({
          agentImage: siteInfo.logo_base64 || "",
          agentName: siteInfo.title
            ? `${siteInfo.title} ${t({
                id: "cx-trial-page.ai-agent",
                defaultMessage: "AI Agent",
              })}`
            : CHAT_BOT_APP_DEFAULT_SETTINGS.agentName,
        }),
      ]
    );

    if (!isBusinessNameUpdated || !isChatBotSettingsUpdated) {
      return;
    }

    await handleTrainAgent(knowledgeSourceId);
  }, [
    handleCreateLinkKnowledge,
    handleTrainAgent,
    handleUpdateBusinessName,
    handleUpdateChatBotSettings,
    link,
    t,
  ]);

  const hasPrefetchedBuildParam = useRef(false);
  useEffect(() => {
    if (
      buildParam &&
      !hasPrefetchedBuildParam.current &&
      !isLinksKnowledgeLoading &&
      !isLinksKnowledgeError &&
      !linkKnowledge
    ) {
      hasPrefetchedBuildParam.current = true;
      handleContinue();
      setSearchParams(
        (prev) => {
          prev.delete("build");
          return prev;
        },
        { replace: true }
      );
    }
  }, [
    buildParam,
    handleContinue,
    handleCreateLinkKnowledge,
    isLinksKnowledgeError,
    isLinksKnowledgeLoading,
    linkKnowledge,
    setSearchParams,
  ]);

  useEffect(() => {
    if (isInvalid && link) {
      setIsInvalid(false);
    }
    if (isLinkInvalid && link && isValidHttpUrl(link, false)) {
      setIsLinkInvalid(false);
    }
  }, [link, isInvalid, isLinkInvalid]);

  return (
    <>
      {isLoaderVisible && (
        <TrialFullPageLoader
          onComplete={() => setIsTrialLoaderFinished(true)}
        />
      )}
      <MultiSectionLayout
        classNames={{ content: "flex flex-col" }}
        className="min-h-screen w-screen [@media(min-width:1280px)]:h-screen"
        leftSide={
          <div className="hidden h-screen w-[960px] max-w-[min(50vw,700px)] overflow-hidden [@media(min-width:1300px)]:flex [@media(min-width:1440px)]:max-w-[min(50vw,960px)]">
            <OverviewVideo />
          </div>
        }
      >
        <DataLoadingWrapper
          isLoading={isLinksKnowledgeLoading || isAgentConfigLoading}
          isError={isLinksKnowledgeError}
          onRetry={linksKnowledgeRefetch}
          className="flex min-h-screen flex-col"
        >
          <div className="relative flex w-full grow flex-col items-center justify-center">
            <Logo className="absolute right-[40px] top-[40px] z-[20]" />
            <div className="flex w-[400px] flex-col items-center justify-center gap-12 py-[110px] [@media(min-width:1280px)]:w-[420px] [@media(min-width:1280px)]:py-6 [@media(min-width:1440px)]:w-[440px] [@media(min-width:1440px)]:gap-14 [@media(min-width:1728px)]:w-[500px]">
              <div className="flex flex-col items-center gap-1.5 text-center">
                <span className="text-dark font-secondary text-center text-[26px] font-semibold leading-[150%] [@media(min-width:1280px)]:text-3xl [@media(min-width:1440px)]:text-[30px] [@media(min-width:1728px)]:text-[34px]">
                  {t({
                    id: "cx-trial-page.build-your-business-ai-agent",
                    defaultMessage: "Build Your Business AI Agent",
                  })}
                </span>
                <span className="text-dark-600 font-secondary text-[13px] leading-[150%] [@media(min-width:1440px)]:text-sm">
                  {t({
                    id: "cx-trial-page.add-website-link",
                    defaultMessage:
                      "Add your Website link to train AI Agent based on it",
                  })}
                </span>
              </div>
              <div className="flex w-full flex-col gap-8">
                <TextInput
                  labelPosition="border-overlap"
                  label={t({
                    id: "cx-trial-page.website-link",
                    defaultMessage: "Website link",
                  })}
                  placeholder="mybusiness.com"
                  value={link}
                  onChange={(e) => setLink(e.target.value.trim())}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleContinue();
                    }
                  }}
                  isInvalid={isLinkInvalid}
                  errorMessage={
                    isLinkInvalid
                      ? t({
                          id: "validation.invalid-url",
                          defaultMessage: "Enter a valid URL address",
                        })
                      : undefined
                  }
                />
                {isInvalid && (
                  <div className="bg-tertiary-50 flex items-center justify-start gap-3 rounded-xl p-3">
                    <AlertIcon className="shrink-0" />
                    <span className="text-dark font-secondary text-sm leading-[140%]">
                      {t({
                        id: "cx-trial-page.validation-website-link",
                        defaultMessage:
                          "Add the Website Link to build AI Agent",
                      })}
                    </span>
                  </div>
                )}
              </div>
              <Button fullWidth onClick={handleContinue}>
                {t({
                  id: "cx-trial-page.continue-button",
                  defaultMessage: "Train & Build",
                })}
              </Button>
            </div>
            <div className="absolute bottom-10 left-1/2 z-[20] flex h-12 -translate-x-1/2 items-center justify-center">
              <InteractiveContainer
                className="text-grey-800 font-secondary text-sm leading-[21px] underline transition-colors hover:text-[#2F80ED]"
                onClick={() => CXSignupDialog?.open()}
              >
                {t({
                  id: "cx-trial-page.sign-up",
                  defaultMessage: "Sign-up",
                })}
              </InteractiveContainer>
            </div>
          </div>
          <div className="min-h-min w-full overflow-hidden [@media(min-width:1300px)]:hidden">
            <OverviewVideo />
          </div>
          {!isLoaderVisible && <TrialHelpWidget />}
        </DataLoadingWrapper>
      </MultiSectionLayout>
    </>
  );
};

export const CXTrialPage: React.FC = () => {
  const { isAuthenticated } = useSelector(selectAuthState);
  const { isAnonymous } = useAnonymousSession();

  if (isAuthenticated) {
    return <NavigateToDefaultEntity />;
  }

  if (!isAnonymous) {
    return (
      <MultiSectionLayout
        className="min-h-screen w-screen [@media(min-width:1280px)]:h-screen"
        leftSide={
          <div className="hidden h-screen w-[960px] max-w-[min(50vw,700px)] overflow-hidden [@media(min-width:1300px)]:flex [@media(min-width:1440px)]:max-w-[min(50vw,960px)]">
            <OverviewVideo />
          </div>
        }
      >
        <DataLoadingWrapper isLoading>
          <div />
        </DataLoadingWrapper>
      </MultiSectionLayout>
    );
  }

  return <CXTrialPageContent />;
};

export default CXTrialPage;
